"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { File } from "@/types/api-v2";
import { fetchObjectFileAsBlob } from "@/lib/object-file-download";
import { Download, FileQuestion, ExternalLink } from "lucide-react";

const IMAGE_MIMES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
const isImage = (mimetype: string) =>
  mimetype.startsWith("image/") || IMAGE_MIMES.includes(mimetype);
const isPdfMime = (mimetype: string) => mimetype === "application/pdf";
const isPdfFile = (file: File) => {
  const m = (file.mimetype ?? "").toLowerCase();
  return isPdfMime(m) || (file.name ?? "").toLowerCase().endsWith(".pdf");
};
const isVideo = (mimetype: string) => mimetype.startsWith("video/");
const isAudio = (mimetype: string) => mimetype.startsWith("audio/");

export function isFilePreviewable(file: File): boolean {
  const m = file.mimetype?.toLowerCase() ?? "";
  return isImage(m) || isPdfMime(m) || (file.name ?? "").toLowerCase().endsWith(".pdf") || isVideo(m) || isAudio(m);
}

export function getPreviewableImages(files: File[]): File[] {
  return files.filter((f) => isImage(f.mimetype?.toLowerCase() ?? ""));
}

/** Загрузчик blob: ОИ — object filestore; ГО/др. — свой API. */
export type FilePreviewFetchBlob = (fileId: number) => Promise<Blob>;

function resolveFetchBlob(
  objectId: number | undefined,
  fetchBlob: FilePreviewFetchBlob | undefined,
): FilePreviewFetchBlob | null {
  if (fetchBlob) return fetchBlob;
  if (objectId != null && objectId > 0) {
    return (fileId) => fetchObjectFileAsBlob(objectId, fileId);
  }
  return null;
}

/** Загрузка файла по API и создание blob URL для просмотра */
function useFileBlobUrl(
  fetchBlob: FilePreviewFetchBlob | null,
  fileId: number,
  enabled: boolean,
  /** Для PDF: явный тип blob, чтобы браузер корректно отображал в iframe (сервер может отдать octet-stream) */
  forceBlobType?: string
) {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !fetchBlob || !fileId) {
      setUrl(null);
      setError(null);
      return;
    }

    let revoked = false;
    setIsLoading(true);
    setError(null);

    fetchBlob(fileId)
      .then((blob) => {
        if (revoked) return;
        const needForceType =
          forceBlobType &&
          (blob.type === "" || blob.type === "application/octet-stream");
        const finalBlob = needForceType
          ? new Blob([blob], { type: forceBlobType })
          : blob;
        setUrl(URL.createObjectURL(finalBlob));
      })
      .catch((e) => {
        if (!revoked) setError(e);
      })
      .finally(() => {
        if (!revoked) setIsLoading(false);
      });

    return () => {
      revoked = true;
      setUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [fetchBlob, fileId, enabled, forceBlobType]);

  return { url, isLoading, error };
}

interface FilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * ID объекта ОИ для загрузки через filestore.
   * Не обязателен, если передан `fetchBlob` (материалы ГО и др.).
   */
  objectId?: number;
  /** Кастомная загрузка blob по id файла/материала (перекрывает objectId). */
  fetchBlob?: FilePreviewFetchBlob;
  /** Список файлов для просмотра (для карусели — только изображения) */
  files: File[];
  /** Индекс файла, с которого начать показ */
  initialIndex?: number;
}

export function FilePreviewDialog({
  open,
  onOpenChange,
  objectId,
  fetchBlob,
  files,
  initialIndex = 0,
}: FilePreviewDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const resolvedFetch = useMemo(
    () => resolveFetchBlob(objectId, fetchBlob),
    [objectId, fetchBlob],
  );

  useEffect(() => {
    if (open) setCurrentIndex(Math.min(initialIndex, Math.max(0, files.length - 1)));
  }, [open, initialIndex, files.length]);

  if (files.length === 0 || !resolvedFetch) return null;

  const currentFile = files[currentIndex];
  const imageFiles = files.filter((f) => isImage((f.mimetype ?? "").toLowerCase()));
  const isCarousel = imageFiles.length > 1 && imageFiles.every((f) => isImage((f.mimetype ?? "").toLowerCase()));

  const content = isCarousel ? (
    <ImageCarousel
      fetchBlob={resolvedFetch}
      files={imageFiles}
      startIndex={imageFiles.findIndex((f) => f.id === currentFile?.id)}
    />
  ) : (
    <SingleFilePreview fetchBlob={resolvedFetch} file={currentFile} />
  );

  const title =
    files.length > 1
      ? `${currentFile?.name ?? "Файл"} (${currentIndex + 1} / ${files.length})`
      : currentFile?.name ?? "Просмотр файла";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto">{content}</div>
      </DialogContent>
    </Dialog>
  );
}

function SingleFilePreview({
  fetchBlob,
  file,
}: {
  fetchBlob: FilePreviewFetchBlob;
  file: File;
}) {
  const forcePdfType = isPdfFile(file) ? "application/pdf" : undefined;
  const { url, isLoading, error } = useFileBlobUrl(
    fetchBlob,
    file.id,
    true,
    forcePdfType
  );
  const m = (file.mimetype ?? "").toLowerCase();

  const handleDownload = useCallback(() => {
    fetchBlob(file.id)
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => {});
  }, [fetchBlob, file.id, file.name]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-muted-foreground">
        Загрузка…
      </div>
    );
  }

  if (error || !url) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] gap-2 text-muted-foreground">
        <FileQuestion className="h-12 w-12" />
        <p>Просмотр недоступен</p>
        <Button variant="outline" size="sm" onClick={handleDownload}>
          <Download className="h-4 w-4 mr-2" />
          Скачать файл
        </Button>
      </div>
    );
  }

  if (isImage(m)) {
    return (
      <div className="flex justify-center bg-muted/30 rounded-md overflow-auto max-h-[70vh]">
        <img
          src={url}
          alt={file.name}
          className="max-w-full max-h-[70vh] object-contain"
        />
      </div>
    );
  }

  if (isPdfFile(file)) {
    return (
      <div className="w-full space-y-2">
        <div className="w-full rounded-md overflow-hidden border bg-muted/30">
          <object
            data={url}
            type="application/pdf"
            className="w-full h-[70vh]"
            title={file.name}
          >
            <div className="flex flex-col items-center justify-center min-h-[200px] gap-2 text-muted-foreground p-4">
              <p className="text-sm text-center">
                Встроенный просмотр PDF недоступен в этом браузере.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Открыть в новой вкладке
              </Button>
            </div>
          </object>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-muted-foreground"
          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Открыть PDF в новой вкладке
        </Button>
      </div>
    );
  }

  if (isVideo(m)) {
    return (
      <div className="flex justify-center rounded-md overflow-hidden bg-black">
        <video src={url} controls className="max-w-full max-h-[70vh]" />
      </div>
    );
  }

  if (isAudio(m)) {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <audio src={url} controls className="w-full max-w-md" />
        <p className="text-sm text-muted-foreground">{file.name}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] gap-2 text-muted-foreground">
      <FileQuestion className="h-12 w-12" />
      <p>Просмотр для данного типа файла недоступен</p>
      <Button variant="outline" size="sm" onClick={handleDownload}>
        <Download className="h-4 w-4 mr-2" />
        Скачать файл
      </Button>
    </div>
  );
}

function ImageCarousel({
  fetchBlob,
  files,
  startIndex,
}: {
  fetchBlob: FilePreviewFetchBlob;
  files: File[];
  startIndex: number;
}) {
  const [index, setIndex] = useState(() => Math.max(0, Math.min(startIndex, files.length - 1)));
  const currentFile = files[index];

  return (
    <div className="space-y-4">
      <Carousel
        opts={{ startIndex: Math.max(0, Math.min(startIndex, files.length - 1)) }}
        setApi={(api) => {
          api?.on("select", () => setIndex(api.selectedScrollSnap()));
        }}
        className="w-full"
      >
        <CarouselContent>
          {files.map((file) => (
            <CarouselItem key={file.id}>
              <SingleImageSlide
                fetchBlob={fetchBlob}
                file={file}
                isActive={file.id === currentFile?.id}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-2" />
        <CarouselNext className="right-2" />
      </Carousel>
      {currentFile && (
        <p className="text-center text-sm text-muted-foreground truncate px-4">
          {currentFile.name}
        </p>
      )}
    </div>
  );
}

function SingleImageSlide({
  fetchBlob,
  file,
  isActive,
}: {
  fetchBlob: FilePreviewFetchBlob;
  file: File;
  isActive: boolean;
}) {
  const { url, isLoading } = useFileBlobUrl(fetchBlob, file.id, isActive);

  if (!isActive) return <div className="min-h-[200px]" />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px] text-muted-foreground">
        Загрузка…
      </div>
    );
  }

  if (!url) return null;

  return (
    <div className="flex justify-center bg-muted/30 rounded-md overflow-auto max-h-[70vh]">
      <img
        src={url}
        alt={file.name}
        className="max-w-full max-h-[70vh] object-contain"
      />
    </div>
  );
}
